import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import type { DipPipelineDryRunResultTarget, DipRunHistoryRow } from "~/features/dataImportProjects/types";
import { toPipelineDryRunResultTarget } from "~/features/dataImportProjects/types";
import { cn } from "~/lib/utils";

interface DipRunHistoryProps {
  rows: DipRunHistoryRow[];
  compact?: boolean;
  onOpenLogs: (jobRunId: string, dryRun?: boolean) => void;
  onOpenDiagnosis: (diagnosisId: string) => void;
  onOpenDryRunResult?: (target: DipPipelineDryRunResultTarget) => void;
}

// Static t() calls so i18next-extract (removeUnusedKeys) keeps these keys.
function useStatusLabels() {
  const { t } = useTranslation();
  return {
    running: t("feature.dataImportProjects.v2.run.history.status.running"),
    completed: t("feature.dataImportProjects.v2.run.history.status.completed"),
    failed: t("feature.dataImportProjects.v2.run.history.status.failed"),
    pending: t("feature.dataImportProjects.v2.run.history.status.pending"),
  } as const;
}

type StatusTone = "score-a" | "score-b" | "score-c" | "default";

function statusTone(status: DipRunHistoryRow["status"]): StatusTone {
  if (status === "completed") return "score-a";
  if (status === "failed") return "score-c";
  if (status === "running" || status === "pending") return "score-b";
  return "default";
}

export function DipRunHistory({
  rows,
  compact = false,
  onOpenLogs,
  onOpenDiagnosis,
  onOpenDryRunResult,
}: DipRunHistoryProps) {
  const { t } = useTranslation();
  const statusLabels = useStatusLabels();
  const actionLabels: Record<DipRunHistoryRow["action"], string> = {
    run: t("ui.run"),
    dryrun: t("ui.dryRun"),
    diagnosis: t("feature.dataImportProjects.diagnosis.label"),
  };

  if (rows.length === 0) {
    return <p className="py-4 text-center text-12-regular text-fg-3">{t("feature.dataImportProjects.v2.run.empty")}</p>;
  }

  return (
    <div className="flex flex-col divide-y divide-border-default">
      {rows.map((row) => {
        const label = statusLabels[row.status as keyof typeof statusLabels] ?? row.status;
        const tone = statusTone(row.status);
        const dryRunResultTarget = row.kind === "pipeline" ? toPipelineDryRunResultTarget(row) : null;

        // Summary shown on the row: job count for pipeline runs, severity counts for diagnoses.
        const summary =
          row.kind === "pipeline"
            ? t("feature.dataImportProjects.v2.run.history.jobsCount", {
                count: row.beJobIds.length + row.bcrJobIds.length,
              })
            : `${row.counts.ok} / ${row.counts.warning} / ${row.counts.error}`;

        return (
          <div
            key={row.id}
            className={cn(
              "flex flex-wrap items-center justify-between gap-x-3 gap-y-1",
              compact ? "px-2 py-1.5" : "px-3 py-2",
            )}
          >
            {/* Info cluster: time, summary, action label, auto-approve, status */}
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
              <span className={cn("shrink-0 tabular-nums text-fg-2", compact ? "text-10-regular" : "text-12-regular")}>
                {t("date.long", { date: parseISO(row.executedAtISO) })}
              </span>
              <span className={cn("text-fg-3", compact ? "text-10-regular" : "text-12-regular")}>{summary}</span>
              {!compact && <span className="text-12-regular text-fg-3">{actionLabels[row.action]}</span>}
              {row.kind === "pipeline" && row.autoApprove && (
                <span className="text-12-regular italic text-fg-3">
                  {t("feature.dataImportProjects.v2.run.history.autoApproveOn")}
                </span>
              )}
              <Badge tone={tone} className="shrink-0">
                {label}
              </Badge>
            </div>

            {/* Action buttons — always visible, no accordion. Details/IDs live in the opened dialogs. */}
            <div className="flex shrink-0 items-center gap-1">
              {row.kind === "pipeline" ? (
                <>
                  <Button variant="ghost" size="sm" onClick={() => onOpenLogs(row.id, row.action === "dryrun")}>
                    {t("ui.logs")}
                  </Button>
                  {dryRunResultTarget && onOpenDryRunResult ? (
                    <Button variant="ghost" size="sm" onClick={() => onOpenDryRunResult(dryRunResultTarget)}>
                      {t("ui.dryRunResult")}
                    </Button>
                  ) : null}
                </>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => onOpenDiagnosis(row.id)}>
                  {t("ui.details")}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
